// Configuración que la interfaz necesita conocer (nada secreto: las llaves nunca salen del servidor).
import { z } from 'zod';
import { Fecha, FormatoFamilia } from './basicos.ts';
import { EstadoIA, EstadoIAPublico } from './ia.ts';
import { PoliticaAcceso } from './usuario.ts';

export const ProveedorAccesoNombre = z.enum(['local', 'firebase', 'smtp']);
export type ProveedorAccesoNombre = z.infer<typeof ProveedorAccesoNombre>;

export const ConfigPublica = z.strictObject({
  version_app: z.string().min(1).max(40),
  acceso: z.strictObject({
    proveedor: ProveedorAccesoNombre,
    /** true = el enlace se enseña en pantalla en vez de mandarse por correo. La interfaz pone una franja roja fija
     *  "MODO DESARROLLO" mientras sea true. El servidor se niega a arrancar así fuera de localhost. */
    modo_desarrollo: z.boolean(),
    /** Solo con proveedor "firebase": lo que el SDK web necesita para completar el enlace (no es secreto). */
    firebase: z.strictObject({ proyecto: z.string(), api_key_web: z.string(), auth_dominio: z.string() }).nullable(),
  }),
  /** El director recibe el estado completo; los demás, solo modo, modelo y un mensaje sin cifras (EstadoIAPublico). */
  ia: z.union([EstadoIA, EstadoIAPublico]),
  pdf: z.strictObject({
    disponible: z.boolean(),
    motivo: z.string().max(300).nullable(),
    /** Formatos que el generador tiene registrados HOY (el resto truena con PDF_FORMATO_NO_EXISTE). */
    formatos: z.array(FormatoFamilia),
    /** Contrato v2: variantes que el generador tiene construidas HOY, tal cual las reporta (DisponibilidadPdf.variantes). */
    variantes: z.array(z.string().max(40)).max(30).optional(),
  }),
  crm_externo: z.strictObject({
    activo: z.boolean(),
    motivo: z.string().max(300),
    /** Nube, tanda 1: la cola PERSISTENTE de envíos al CRM de Carlos (sobrevive a que la instancia se apague). Solo al
     *  director. `fallidos` > 0 = la interfaz lo enseña en rojo (nunca se pierde un envío en silencio). */
    cola: z
      .strictObject({ pendientes: z.number().int().min(0), fallidos: z.number().int().min(0), mas_antiguo: z.string().max(40).nullable() })
      .optional(),
  }),
  almacenamiento: z.strictObject({
    base: z.enum(['local', 'datastore']),
    archivos: z.enum(['local', 'nube']),
    /** Nube, tanda 1: true = ENSAYO en Cloud Run con disco en memoria: lo capturado se BORRA al apagarse la instancia.
     *  La interfaz pone una franja roja fija mientras sea true. */
    efimero: z.boolean().optional(),
    /** Nube, tanda 2 · fusible de lecturas de Datastore (LecturasBase en api.ts). Cualquier estado en true = la interfaz
     *  pone una franja roja fija; null/ausente = base local (no cobra lecturas). */
    lecturas: z
      .strictObject({
        fecha: Fecha,
        hoy: z.int().min(0),
        anonimas: z.int().min(0),
        umbral: z.int().min(1),
        saltado: z.boolean(),
        detenido: z.boolean(),
        entradas_cerradas: z.boolean(),
        apagado: z.boolean(),
      })
      .nullable()
      .optional(),
  }),
  /** Contrato v2: política de acceso vigente (hoy APAGADA: todos editan todo). Opcional mientras el servidor se migra;
   *  la interfaz usa puedeEnCotizacion(usuario, accion, cotizacion, politica ?? POLITICA_ACCESO_POR_OMISION). */
  politica_acceso: PoliticaAcceso.optional(),
});
export type ConfigPublica = z.infer<typeof ConfigPublica>;
